/**
 * AgentRuntimeProvider — Bridges GeminiAgentRuntime to assistant-ui.
 *
 * Converts GeminiAgentRuntime conversation state into assistant-ui's
 * ThreadMessageLike format and provides it via AssistantRuntimeProvider.
 *
 * Confirmation flow:
 *   1. GeminiAgentRuntime returns confirmation -> setPendingConfirmation
 *   2. ConfirmationCard rendered inline via AgentPanel
 *   3. handleApprove/handleReject callbacks resolve the pending action
 *
 * Streaming:
 *   onChunk callback incrementally appends text to the current message.
 */

import { useState, useCallback, useRef, useEffect, createContext, useContext, type ReactNode } from "react"
import {
  useExternalStoreRuntime,
  AssistantRuntimeProvider,
} from "@assistant-ui/react"
import type { ThreadMessageLike, AppendMessage } from "@assistant-ui/react"
import {
  GeminiAgentRuntime,
  type AgentPersona,
  type ConfirmationResult,
  type AgentContentPart,
} from "@cogo/webmcp-runtime"

interface Props {
  apiKey: string
  persona: AgentPersona
  children: ReactNode
}

/** Structured metadata attached to assistant messages for custom rendering. */
export interface AgentMessageMeta {
  toolCalls?: Array<{
    toolName: string
    args: Record<string, unknown>
    result: unknown
    success: boolean
    error?: string
  }>
  confirmation?: ConfirmationResult
}

let msgCounter = 0
function genId() {
  return `msg-${++msgCounter}-${Date.now()}`
}

/** Convert AgentContentParts to ThreadMessageLike content + metadata. */
function buildAssistantMessage(
  parts: AgentContentPart[],
  confirmation?: ConfirmationResult,
): ThreadMessageLike {
  const textParts: Array<{ type: "text"; text: string }> = []
  const toolCalls: AgentMessageMeta["toolCalls"] = []

  for (const part of parts) {
    if (part.type === "text") {
      textParts.push({ type: "text", text: part.text })
    } else {
      toolCalls.push({
        toolName: part.toolName,
        args: part.args,
        result: part.result,
        success: part.success,
        error: part.error,
      })
      // Include a text representation for tool calls (ToolCallBadge renders this)
      const status = part.success ? "ok" : `error: ${part.error ?? "unknown"}`
      textParts.push({
        type: "text",
        text: `[TOOL_CALL]${JSON.stringify({ toolName: part.toolName, success: part.success, error: part.error })}`,
      })
    }
  }

  // Confirmation is rendered directly by AgentPanel via pendingConfirmation prop,
  // not as a message marker (markdown parser breaks [CONFIRMATION] markers).

  // Ensure at least one text part
  if (textParts.length === 0) {
    textParts.push({ type: "text", text: "" })
  }

  return {
    role: "assistant",
    id: genId(),
    content: textParts,
  }
}

const SESSION_KEY = "cogo-agent-history"

function loadHistory(): ThreadMessageLike[] {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveHistory(messages: ThreadMessageLike[]) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages))
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

export function AgentRuntimeProvider({ apiKey, persona, children }: Props) {
  const [messages, setMessages] = useState<ThreadMessageLike[]>(loadHistory)
  const [isRunning, setIsRunning] = useState(false)
  const geminiRef = useRef(new GeminiAgentRuntime({ apiKey }))
  const [pendingConfirmation, setPendingConfirmation] =
    useState<ConfirmationResult | null>(null)
  const [streamingText, setStreamingText] = useState("")

  // Persist messages to sessionStorage on every change
  useEffect(() => {
    saveHistory(messages)
  }, [messages])

  // Restore Gemini conversation history from saved messages (text-only, no tool calls)
  useEffect(() => {
    const saved = loadHistory()
    if (saved.length > 0) {
      const contents = saved
        .map((m) => ({
          role: (m.role === "user" ? "user" : "model") as "user" | "model",
          parts: (m.content as Array<{ type: string; text: string }>)
            .filter((c) => c.type === "text" && !c.text.startsWith("[TOOL_CALL]") && c.text.trim().length > 0)
            .map((c) => ({ text: c.text })),
        }))
        .filter((c) => c.parts.length > 0)

      // Only restore the last 10 messages to prevent oversized context
      const trimmed = contents.slice(-10)

      // Validate alternating user/model pattern (Gemini requires this)
      const valid: typeof trimmed = []
      for (const msg of trimmed) {
        const last = valid[valid.length - 1]
        if (!last || last.role !== msg.role) {
          valid.push(msg)
        }
        // Skip consecutive same-role messages (invalid for Gemini)
      }

      if (valid.length > 0) {
        geminiRef.current.setContents(valid)
      }
    }
  }, [])

  const getTools = useCallback(() => {
    const cogo = (window as any).__cogo_tools__
    if (!cogo?.getAllTools) return cogo?.getManifest?.() ?? []
    return cogo.getAllTools()
  }, [])

  const callTool = useCallback(
    async (name: string, params: Record<string, unknown>) => {
      const cogo = (window as any).__cogo_tools__
      if (!cogo?.callTool) throw new Error("Cogo runtime not ready")
      return cogo.callTool(name, params)
    },
    [],
  )

  const handleApprove = useCallback(async () => {
    if (!pendingConfirmation) return
    const { callId } = pendingConfirmation
    setPendingConfirmation(null)
    setIsRunning(true)
    try {
      const result = await geminiRef.current.resumeAfterConfirmation(callId, true)
      setMessages((prev) => [...prev, buildAssistantMessage(result.contentParts)])
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          id: genId(),
          content: [{ type: "text", text: "Something went wrong executing the action." }],
        },
      ])
    }
    setIsRunning(false)
  }, [pendingConfirmation])

  const handleReject = useCallback(async () => {
    if (!pendingConfirmation) return
    const { callId } = pendingConfirmation
    setPendingConfirmation(null)
    setIsRunning(true)
    try {
      // Resume with rejection — Gemini will suggest alternatives
      const result = await geminiRef.current.resumeAfterConfirmation(callId, false)
      setMessages((prev) => [...prev, buildAssistantMessage(result.contentParts)])
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          id: genId(),
          content: [{ type: "text", text: "Action skipped." }],
        },
      ])
    }
    setIsRunning(false)
  }, [pendingConfirmation])

  const onNew = useCallback(
    async (msg: AppendMessage) => {
      const text = msg.content.find((c) => c.type === "text")?.text ?? ""
      if (!text.trim()) return

      // Guard: prevent sending while agent is processing
      if (isRunning) return

      setMessages((prev) => [
        ...prev,
        { role: "user", id: genId(), content: [{ type: "text", text }] },
      ])
      setIsRunning(true)
      setStreamingText("")

      try {
        const tools = getTools()
        if (!tools || tools.length === 0) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              id: genId(),
              content: [{ type: "text", text: "App tools not ready yet. Please wait for the app to finish loading." }],
            },
          ])
          setIsRunning(false)
          return
        }

        const result = await geminiRef.current.run(
          text,
          tools,
          persona,
          callTool,
          // Streaming callback: incrementally update the streaming text
          (chunk: string) => {
            setStreamingText((prev) => prev + chunk)
          },
        )

        // Clear streaming text and set final message
        setStreamingText("")
        setMessages((prev) => [
          ...prev,
          buildAssistantMessage(result.contentParts, result.confirmation),
        ])

        if (result.confirmation) {
          setPendingConfirmation(result.confirmation)
        }
      } catch {
        setStreamingText("")
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            id: genId(),
            content: [{ type: "text", text: "I'm having trouble connecting. Check your internet connection and try again." }],
          },
        ])
      }
      setIsRunning(false)
    },
    [persona, getTools, callTool, isRunning],
  )

  const handleSetMessages = useCallback(
    (msgs: readonly ThreadMessageLike[]) => {
      setMessages([...msgs])
    },
    [],
  )

  const convertMessage = useCallback(
    (msg: ThreadMessageLike): ThreadMessageLike => msg,
    [],
  )

  const runtime = useExternalStoreRuntime({
    messages,
    setMessages: handleSetMessages,
    convertMessage,
    onNew,
    isRunning,
  })

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <AgentUIContext.Provider
        value={{ pendingConfirmation, handleApprove, handleReject, streamingText }}
      >
        {children}
      </AgentUIContext.Provider>
    </AssistantRuntimeProvider>
  )
}

// ── Context for confirmation/streaming state ────────────────

interface AgentUIState {
  pendingConfirmation: ConfirmationResult | null
  handleApprove: () => void
  handleReject: () => void
  streamingText: string
}

const AgentUIContext = createContext<AgentUIState>({
  pendingConfirmation: null,
  handleApprove: () => {},
  handleReject: () => {},
  streamingText: "",
})

export function useAgentUI(): AgentUIState {
  return useContext(AgentUIContext)
}

export { type ConfirmationResult }
